import { request } from './client';
import type {
  LearningPlan,
  LearningSettings,
  LearningSettingsInput,
  LearningStats,
  LeechReport,
} from './types';

export interface PlanOptions {
  timeZone: string;
  days?: 30 | 90;
  signal?: AbortSignal;
}

/** Returns the time budget, today's share of it and the forecast. */
export function getLearningPlan(options: PlanOptions): Promise<LearningPlan> {
  const params = new URLSearchParams({
    time_zone: options.timeZone,
    days: String(options.days ?? 30),
  });

  return request<LearningPlan>(
    `/learning/plan?${params.toString()}`,
    options.signal ? { signal: options.signal } : {},
  );
}

/** Replaces the daily time budget and the exam date. */
export function updateLearningSettings(body: LearningSettingsInput): Promise<LearningSettings> {
  return request<LearningSettings>('/learning/settings', { method: 'PUT', body });
}

/** Turns the evening email for a day without study on or off. */
export function updateLearningReminders(remindByEmail: boolean): Promise<LearningSettings> {
  return request<LearningSettings>('/learning/reminders', {
    method: 'PUT',
    body: { remind_by_email: remindByEmail },
  });
}

/** Returns what the dashboard shows. */
export function getLearningStats(options: {
  timeZone: string;
  signal?: AbortSignal;
}): Promise<LearningStats> {
  const params = new URLSearchParams({ time_zone: options.timeZone });

  return request<LearningStats>(
    `/learning/stats?${params.toString()}`,
    options.signal ? { signal: options.signal } : {},
  );
}

/** Returns the cards that keep being forgotten, of one deck or of all. */
export function getLeeches(options: {
  deckId?: string;
  signal?: AbortSignal;
}): Promise<LeechReport> {
  const query = options.deckId
    ? `?${new URLSearchParams({ deck_id: options.deckId }).toString()}`
    : '';

  return request<LeechReport>(
    `/learning/leeches${query}`,
    options.signal ? { signal: options.signal } : {},
  );
}

/** Forgets what was learned about a card, so that it is studied again as new. */
export function resetCard(cardId: string): Promise<void> {
  return request<undefined>(`/cards/${encodeURIComponent(cardId)}/reset`, { method: 'POST' });
}
